// Measures how wide a button actually needs to be: for a single-line
// label this is just its natural text width, for a wrapped label it's the
// widest rendered line. Returns the line count too, so callers can tell
// whether this button wrapped at all.
const measureButtonContentWidth = (btn) => {
  const textNode = Array.from(btn.childNodes).find(
    (node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim() !== ''
  );
  const btnStyle = getComputedStyle(btn);
  const horizontalExtra =
    parseFloat(btnStyle.paddingLeft) +
    parseFloat(btnStyle.paddingRight) +
    parseFloat(btnStyle.borderLeftWidth) +
    parseFloat(btnStyle.borderRightWidth);

  // button--link styles render an arrow icon next to the label (see
  // .button-arrow in base.css) - it takes up real horizontal space that
  // isn't part of the text node, so it has to be added on top or the
  // arrow gets clipped/pushed onto its own line once we set an explicit
  // width below.
  const arrow = btn.querySelector('.button-arrow');
  let arrowWidth = 0;
  if (arrow) {
    const arrowStyle = getComputedStyle(arrow);
    arrowWidth = arrow.getBoundingClientRect().width + parseFloat(arrowStyle.marginLeft) + parseFloat(arrowStyle.marginRight);
  }

  if (!textNode) {
    return { lineCount: 1, contentWidth: btn.getBoundingClientRect().width };
  }

  const range = document.createRange();
  range.selectNodeContents(textNode);
  const lineRects = Array.from(range.getClientRects());
  const maxLineWidth = lineRects.reduce((max, rect) => Math.max(max, rect.width), 0);
  return { lineCount: lineRects.length, contentWidth: maxLineWidth + horizontalExtra + arrowWidth };
};

// Sizes stacked buttons to the width they actually need instead of
// stretching them to fill the container (the default flex cross-axis
// behavior in column layouts) - if there's more than one button in the
// group, they all get sized to whichever one needs the most room, so they
// stay equal width; a lone button just gets its own natural width.
const equalizeStackedButtonWidths = (bannerBtns, container) => {
  const measurements = Array.from(bannerBtns).map((btn) => ({ btn, ...measureButtonContentWidth(btn) }));
  const targetWidth = Math.ceil(Math.max(...measurements.map((entry) => entry.contentWidth)));
  // Read the container's own computed justify-content rather than
  // text-align: every alignment variant (banner, image-with-text, rich
  // text - including rich text's mobile-only *_mobile classes) sets
  // justify-content to control button alignment, but not all of them set
  // text-align, so text-align isn't a reliable signal here.
  const justifyContent = getComputedStyle(container).justifyContent;
  const alignSelf = justifyContent === 'flex-start' ? 'flex-start' : justifyContent === 'flex-end' ? 'flex-end' : 'center';
  measurements.forEach(({ btn }) => {
    btn.style.width = targetWidth + 'px';
    btn.style.maxWidth = '100%';
    btn.style.alignSelf = alignSelf;
  });
};

const bannerBtnWidthAdjust = () => {
  const bannerBtnContainers = document.querySelectorAll(
    '.banner__buttons-inner, .banner__button-container-inner, .image-with-text__button, .rich-text__buttons'
  );
  if (bannerBtnContainers.length === 0) return;
  bannerBtnContainers.forEach((container) => {
    const bannerBtns = container.querySelectorAll('.button');
    // Reset styles left over from a previous run before re-measuring -
    // otherwise a container/button that no longer needs constraining (e.g.
    // resized from a narrow wrapped state to a wide unwrapped one) would
    // keep the stale inline width/flex-grow from last time.
    container.style.width = '';
    bannerBtns.forEach((btn) => {
      btn.style.width = '';
      btn.style.maxWidth = '';
      btn.style.alignSelf = '';
      btn.style.flexGrow = '';
    });

    // Single-button containers (e.g. image-with-text, rich-text) never sit
    // in a row of siblings that could wrap onto a new row, so there's
    // nothing to "sync" - just shrink this button if its own label wraps.
    if (bannerBtns.length <= 1) {
      equalizeStackedButtonWidths(bannerBtns, container);
      return;
    }

    // For multi-button containers, read the container's actual computed
    // flex-direction instead of guessing a viewport width - this always
    // matches whatever breakpoint each component's own CSS defines.
    const isColumnStacked = getComputedStyle(container).flexDirection === 'column';
    if (isColumnStacked) {
      bannerBtns.forEach((btn) => {
        btn.style.flexGrow = 0;
      });
      equalizeStackedButtonWidths(bannerBtns, container);
      return;
    }

    // Row mode: buttons sit side by side and only need adjusting if they
    // wrapped onto more than one row (detected via differing offsetTop
    // among siblings).
    const containerTop = container.offsetTop;
    let resizeBtnTrigger = false;
    let maxBtnWidth = 0;
    bannerBtns.forEach((btn) => {
      const btnTop = btn.offsetTop;
      resizeBtnTrigger = btnTop !== containerTop ? true : false
      const btnRect = btn.getBoundingClientRect();
      const btnWidth = btnRect.width;
      btnWidth > parseFloat(maxBtnWidth) ? (maxBtnWidth = btnWidth) : null;
    });
    if (resizeBtnTrigger === false) return;
    const availableWidth = container.parentElement ? container.parentElement.clientWidth : maxBtnWidth;
    const finalWidth = Math.min(maxBtnWidth, availableWidth);
    container.style.width = finalWidth + 'px';
    bannerBtns.forEach((btn) => {
      btn.style.flexGrow = 1;
    });
  });
};

// Buttons are hidden via CSS (see base.css `.js .banner__buttons-inner`)
// until this runs once, so the initial width/alignment calculation never
// becomes visible to the user as a layout jump - we just fade it in after.
const revealBannerButtons = () => {
  document
    .querySelectorAll(
      '.banner__buttons-inner, .banner__button-container-inner, .image-with-text__button, .rich-text__buttons'
    )
    .forEach((container) => {
      container.classList.add('banner-buttons-ready');
    });
};

const runBannerBtnAdjustments = () => {
  try {
    bannerBtnWidthAdjust();
  } finally {
    revealBannerButtons();
  }
};

domReady(() => {
  runBannerBtnAdjustments();
  window.addEventListener('resize', bannerBtnWidthAdjust);
  // The theme editor swaps a section's markup via AJAX (adding/reordering
  // blocks, changing settings) without a full page load, so freshly
  // injected buttons would otherwise be stuck at opacity:0 forever since
  // they'd never pick up the "banner-buttons-ready" class.
  document.addEventListener('shopify:section:load', runBannerBtnAdjustments);
});